import { useEffect, useCallback } from 'react'
import { useWorkspaceSocket } from '@/hooks/workspace/useWorkspaceSocket'
import { useProjectDataFull } from '@/hooks/workspace/useProjectDataFull'
import { useGraphRefresh } from '@/hooks/workspace/useGraphRefresh'
import { useWorkspaceStore } from '@/store/workspaceStore'
import { WorkspaceLayout } from '@/components/workspace/layout/WorkspaceLayout'
import { WorkspaceHeader } from '@/components/workspace/layout/WorkspaceHeader'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { FloatingReactions } from '@/components/workspace/FloatingReactions'
import { useUserTier } from '@/lib/api/hooks'
import { lcaAPI } from '@/lib/api/client'
import { getApiErrorMessage } from '@/lib/api/errors'
import { toast } from 'sonner'

export default function LiveWorkspace() {
  const {
    events,
    connected,
    connecting,
    error: wsError,
    spectatorCount,
    reactions,
    connect,
    clearEvents,
  } = useWorkspaceSocket()

  const {
    project,
    activities,
    loading: projectLoading,
    error: projectError,
    loadProject,
    refreshProject,
  } = useProjectDataFull()

  const { selectedProjectId, handleProjectSelect: hookProjectSelect } = useGraphRefresh({
    events,
    refreshProject,
    loadProject,
  })

  const { selectNode, setCurrentProject, setCenterTab, selectedNodeId, selectedNodeType } =
    useWorkspaceStore()
  const { data: userTierData } = useUserTier()

  // Auto-connect WebSocket on mount
  useEffect(() => {
    connect()
  }, [connect])

  // Sync store when hook auto-selects a project
  useEffect(() => {
    if (selectedProjectId) {
      setCurrentProject(selectedProjectId)
    }
  }, [selectedProjectId, setCurrentProject])

  // Manual project selection — goes through the hook so polling tracks it.
  // Also reset the center tab to Model so the user lands on the graph for a
  // freshly-picked project. Done only on EXPLICIT selection — the auto-restore
  // path on mount preserves whatever tab the user last left active.
  const handleProjectSelect = (projectId: string) => {
    hookProjectSelect(projectId)
    setCurrentProject(projectId)
    setCenterTab('graph')
  }

  const handleNodeClick = (nodeId: string, nodeType: 'activity' | 'exchange') => {
    selectNode(nodeId, nodeType)
  }

  const handleExport = useCallback(
    async (format: 'ecospold1' | 'csv') => {
      if (!project?.id) return
      try {
        const res = await lcaAPI.exportProject(project.id, format)
        const { download_url, filename } = res.data
        const a = document.createElement('a')
        a.href = download_url
        a.download =
          filename || `${project.project_name || 'project'}.${format === 'csv' ? 'csv' : 'xml'}`
        a.click()
        toast.success('Export ready — download started')
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Export failed'))
      }
    },
    [project],
  )

  return (
    <TooltipProvider delayDuration={300}>
      <div className="h-screen flex flex-col bg-background overflow-hidden">
        {/* Header */}
        <WorkspaceHeader
          projectName={project?.project_name}
          projectDatabase={project?.database}
          spectatorCount={spectatorCount}
          isAdmin={!!userTierData?.is_superuser}
          hasProject={!!project}
          onRefresh={refreshProject}
          onExport={handleExport}
          mode="modeling"
        />

        {/* Error alerts */}
        {(wsError || projectError) && (
          <div className="px-4 py-2 space-y-2">
            {wsError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Connection Error</AlertTitle>
                <AlertDescription>{wsError}</AlertDescription>
              </Alert>
            )}
            {projectError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Project Error</AlertTitle>
                <AlertDescription>{projectError}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Main workspace layout */}
        <div className="flex-1 min-h-0 relative">
          <FloatingReactions reactions={reactions} />
          <WorkspaceLayout
            activities={activities}
            projectName={project?.project_name}
            projectDatabase={project?.database}
            selectedProjectId={selectedProjectId}
            selectedNodeId={selectedNodeId}
            selectedNodeType={selectedNodeType}
            onProjectSelect={handleProjectSelect}
            onNodeClick={handleNodeClick}
            onExchangeCreated={refreshProject}
            onActivityCreated={refreshProject}
            isLoading={projectLoading}
            wsEvents={events}
            wsConnected={connected}
            wsConnecting={connecting}
            onClearEvents={clearEvents}
          />
        </div>
      </div>
    </TooltipProvider>
  )
}
